import { Link } from "react-router-dom";
import { ArrowRight, Download, Check } from "lucide-react";
import { Button } from "../components/ui/button";

export const HomePage = ({ onOpenCatalogModal }) => {
  return (
    <div className="bg-cream">
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6 animate-fade-in-up">
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-forest leading-tight">
                Premium Jute & Cotton Bag{" "}
                <span className="text-sand">Manufacturer</span> Since 2006
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg">
                Serving Global Retailers, Importers & Private Labels with
                eco-friendly, export-quality bags crafted with integrity.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/products">
                  <Button
                    data-testid="hero-view-collection-btn"
                    className="bg-forest hover:bg-forest-dark text-white font-medium rounded-full px-8 py-6 transition-all duration-300 hover:shadow-hover"
                  >
                    View Collection
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Button
                  data-testid="hero-download-catalog-btn"
                  onClick={onOpenCatalogModal}
                  variant="outline"
                  className="border-2 border-sand text-sand hover:bg-sand hover:text-white font-medium rounded-full px-8 py-6 transition-all duration-300"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Catalog
                </Button>
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-fade-in-up animation-delay-200">
              <div className="relative rounded-2xl overflow-hidden shadow-hover">
                <img
                  src={process.env.PUBLIC_URL + "/jute.jpeg"}
                  alt="Premium Jute Bags"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/20 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Brands Section */}
      <section data-testid="brands-section" className="py-16 md:py-24 bg-cream-dark">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest uppercase tracking-wider">
              Our Brands
            </h2>
            <div className="w-24 h-1 bg-sand mx-auto mt-4" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Bags4Ever Card */}
            <div
              data-testid="brand-bags4ever"
              className="bg-white rounded-xl p-8 border border-sand/20 shadow-card hover-lift"
            >
              <div className="mb-4 flex justify-center">
                <img
                  src={process.env.PUBLIC_URL + "/bagsforever.png"}
                  alt="Bags4Ever - Sustainable by Nature"
                  className="h-20 md:h-24 w-auto object-contain"
                />
              </div>
              <p className="text-muted-foreground mb-6">
                Eco-Friendly Jute & Cotton Bags
              </p>
              <a
                href="https://bags4ever.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center border-2 border-forest text-forest hover:bg-forest hover:text-white rounded-full px-6 py-2 transition-all duration-300 font-medium"
              >
                Explore Bags4Ever
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </div>

            {/* NAVADYA Card */}
            <div
              data-testid="brand-navadya"
              className="bg-white rounded-xl p-8 border border-sand/20 shadow-card hover-lift"
            >
              <div className="mb-3 flex justify-center">
                <img
                  src={process.env.PUBLIC_URL + "/navada.png"}
                  alt="Navadya"
                  className="h-20 md:h-24 w-auto object-contain"
                />
              </div>
              <p className="text-muted-foreground mb-6">
                Premium Botanical Lifestyle
              </p>
              <Button
                variant="outline"
                className="border-2 border-forest text-forest hover:bg-forest hover:text-white rounded-full px-8 py-4 transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
              >
                Discover NAVADYA (Coming Soon)
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Quality & Manufacturing Section */}
      <section data-testid="quality-section" className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-forest leading-tight">
                Crafted for{" "}
                <span className="font-accent text-[1.15em] font-bold tracking-[2px]">
                  Quality.
                </span>
                <br />
                Delivered with{" "}
                <span className="font-accent text-[1.15em] font-bold tracking-[2px]">
                  Integrity.
                </span>
              </h2>
              <ul className="space-y-4">
                {[
                  "OEM & Private Label Manufacturing",
                  "Custom Printing & Design",
                  "Export-Grade Packaging",
                  "Scalability for Large Orders",
                  "Ethical & Certified Production",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-forest/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-forest" />
                    </div>
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-hover">
                <img
                  src={process.env.PUBLIC_URL + "/jute2.jpeg"}
                  alt="Jute bag manufacturing"
                  className="w-full h-[400px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global Market Trust Section */}
      <section data-testid="global-trust-section" className="py-16 md:py-24 bg-cream-dark">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-forest mb-12">
            Trusted by International Markets Since 2006
          </h2>

          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {[
              {
                name: "USA",
                code: "US",
                flagUrl: "https://flagcdn.com/us.svg",
              },
              {
                name: "Europe",
                code: "EU",
                flagUrl: "https://flagcdn.com/eu.svg",
              },
              {
                name: "Australia",
                code: "AU",
                flagUrl: "https://flagcdn.com/au.svg",
              },
              {
                name: "Middle East",
                code: "AE",
                flagUrl: "https://flagcdn.com/ae.svg",
              },
            ].map((country, index) => (
              <div
                key={index}
                data-testid={`country-${country.name.toLowerCase().replace(" ", "-")}`}
                className="flex flex-col items-center gap-2"
              >
                <div className="w-16 h-10 rounded-md overflow-hidden shadow-sm border border-forest/10 bg-white">
                  <img
                    src={country.flagUrl}
                    alt={`${country.name} flag`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <span className="text-xs font-semibold tracking-[0.2em] text-forest">
                  {country.code}
                </span>
                <span className="text-sm font-medium text-muted-foreground">
                  {country.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-16 md:py-20 bg-[#1a1a1a]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-8">
            Looking for a Reliable Wholesale Partner?
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              data-testid="cta-download-btn"
              onClick={onOpenCatalogModal}
              className="bg-forest hover:bg-forest-dark text-white font-medium rounded-full px-8 py-6 transition-all duration-300"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Product Catalog
            </Button>
            <Link to="/contact">
              <Button
                data-testid="cta-quote-btn"
                className="bg-sand hover:bg-sand-dark text-white font-medium rounded-full px-8 py-6 transition-all duration-300"
              >
                Request a Quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
